import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAdventContests } from "../../features/competition/competitionSlice";
import { useNavigate } from "react-router-dom";

const WeekSelector = ({ weeks, activeWeek, onSelectWeek }) => (
  <div className="w-full overflow-x-auto mb-8">
    <div className="flex space-x-4 min-w-max p-4 justify-center">
      {weeks.map((week) => (
        <button
          key={week.id}
          onClick={() => onSelectWeek(week.id)}
          className={`px-6 py-3 rounded-2xl transition-all duration-300 font-bold text-lg border-4 ${
            activeWeek === week.id
              ? "bg-gradient-to-br from-[#D32F2F] to-[#A91D1D] text-white border-[#FFD700] scale-105 shadow-xl"
              : "bg-white text-[#3E2723] border-[#FFD700] hover:scale-105 hover:shadow-lg hover:-rotate-2"
          }`}
        >
          {week.name}
        </button>
      ))}
    </div>
  </div>
);

const getSubmissionStatusBadge = (submission, isUnlocked) => {
  if (!submission) {
    return (
      <span className="px-4 py-2 rounded-full text-sm font-extrabold bg-[#E8E8E8] text-[#999] border-3 border-[#CCC]">
        Not Started
      </span>
    );
  }

  const statusConfig = {
    Accepted: {
      bgColor: "bg-[#FFD700]",
      textColor: "text-[#3E2723]",
      borderColor: "border-[#FFF9C4]",
      label: "Completed ⭐",
    },
    "In Progress": {
      bgColor: "bg-gradient-to-br from-[#2E7D32] to-[#1B5E20]",
      textColor: "text-white",
      borderColor: "border-[#FFD700]",
      label: "Available 🎁",
    },
    Failed: {
      bgColor: "bg-[#E8E8E8]",
      textColor: "text-[#999]",
      borderColor: "border-[#CCC]",
      label: "Try Again",
    },
  };

  const config = statusConfig[submission.status] || statusConfig["In Progress"];

  return (
    <div className="space-y-1.5">
      <span
        className={`inline-block px-4 py-2 rounded-full text-sm font-extrabold border-3 ${config.bgColor} ${config.textColor} ${config.borderColor} shadow-md`}
      >
        {config.label}
      </span>
      {submission.attemptCounter > 0 && (
        <div className="text-xs text-[#3E2723] font-bold">
          {submission.attemptCounter} attempt
          {submission.attemptCounter !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};

const ChallengeGrid = ({ challenges, contestTag }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const isChallengeUnlocked = (challenge) => {
    
    if (!user.length) return false;

    if (!challenge.releaseDate) return true;
    
    if (user[0]?.isAdmin) return true;
    const now = new Date();
    const releaseDate = new Date(challenge.releaseDate);
    return now >= releaseDate;
  };

  const handleChallengeClick = (challenge) => {
    if (!isChallengeUnlocked(challenge)) return;
    navigate(`/advent/${contestTag}/${challenge.slug}`);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {challenges.map((challenge, index) => {
        const rotation = index % 2 === 0 ? "-rotate-1" : "rotate-1";
        const isUnlocked = isChallengeUnlocked(challenge);

        return (
          <div key={challenge._id} className="group relative">
            <div
              onClick={() => handleChallengeClick(challenge)}
              className={`relative bg-white p-8 rounded-3xl border-5 border-[#FFD700] shadow-xl
                hover:shadow-2xl transition-all duration-400 flex flex-col min-h-[300px]
                ${rotation} hover:rotate-0 hover:scale-105 hover:border-[#D32F2F]
                ${
                  isUnlocked
                    ? "cursor-pointer"
                    : "cursor-not-allowed opacity-80"
                }
                overflow-hidden`}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleChallengeClick(challenge);
                }
              }}
            >
              {/* Decorative stripe at top */}
              <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-r from-[#D32F2F] via-[#2E7D32] via-[#FFD700] to-[#D32F2F]"></div>

              {/* Header Section */}
              <div className="flex justify-between items-start gap-4 mb-4 mt-2">
                <div className="font-['Cabin_Sketch'] text-4xl md:text-5xl text-[#D32F2F] font-bold text-shadow-vintage">
                  Day {challenge.order || "?"}
                </div>
                <div className="flex-shrink-0">
                  {getSubmissionStatusBadge(challenge.userSubmission, isUnlocked)}
                </div>
              </div>

              {/* Title */}
              <h3 className="font-['Covered_By_Your_Grace'] text-2xl md:text-3xl text-[#1B5E20] mb-4 leading-tight">
                {challenge.name}
              </h3>

              {/* Stats Section */}
              <div className="flex-grow flex flex-col justify-end mt-auto">
                <div className="flex items-baseline gap-4 border-t-3 border-dashed border-[#FFF9C4] pt-4">
                  <div className="flex-1">
                   
                    <div className="bg-gradient-to-br from-[#FFF9C4] to-[#FFD700] text-[#3E2723] px-4 py-2 rounded-full font-extrabold text-xl inline-block border-3 border-[#FFD700] shadow-md">
                      ⭐ {challenge.points}
                    </div>
                  </div>

                  {challenge.userSubmission?.pointsEarned > 0 && (
                    <div>
                      <div className="text-sm font-bold text-[#2E7D32] uppercase tracking-wider">
                        Earned
                      </div>
                      <div className="bg-[#FFD700] text-[#3E2723] px-4 py-2 rounded-full font-extrabold text-xl mt-2 inline-block border-3 border-[#FFF9C4] shadow-md">
                        {challenge.userSubmission.pointsEarned}
                      </div>
                    </div>
                  )}
                </div>

                {challenge.userSubmission?.lastSubmissionDate && (
                  <div className="text-xs text-[#3E2723] mt-3 font-semibold">
                    Last attempt:{" "}
                    {new Date(
                      challenge.userSubmission.lastSubmissionDate
                    ).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>

            {/* Login/Locked Tooltip */}
            {!isUnlocked && (
              <div
                className="absolute -top-12 left-1/2 transform -translate-x-1/2 w-72 opacity-0
                            group-hover:opacity-100 transition-all duration-200 pointer-events-none z-10"
              >
                <div className="bg-[#3E2723] text-white text-sm py-2 px-4 rounded-xl shadow-xl border-2 border-[#FFD700]">
                  <div className="text-center font-semibold">
                    {!user.length
                      ? "Login to complete the challenge"
                      : challenge.releaseDate
                      ? `Unlocks ${new Date(challenge.releaseDate).toLocaleDateString()} at ${new Date(challenge.releaseDate).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                      : "Challenge not available yet"}
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 translate-y-2">
                    <div className="border-8 border-transparent border-t-[#3E2723]" />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const LoadingState = () => (
  <div className="flex items-center justify-center h-64">
    <div className="text-[#D32F2F] text-xl font-bold animate-pulse">
      Loading challenges... 🎄
    </div>
  </div>
);

const ErrorState = ({ error }) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-[#D32F2F] text-xl font-bold">
      Error loading challenges: {error}
    </div>
  </div>
);

const ChallengesSection = () => {
  const [activeWeek, setActiveWeek] = useState(1);
  const dispatch = useDispatch();

  const { adventContests, showSpinner, generalError } = useSelector(
    (state) => state.competitions
  );

  React.useEffect(() => {
    dispatch(getAdventContests("advent-2025"));
  }, [dispatch]);

  if (showSpinner) return <LoadingState />;
  if (generalError) return <ErrorState error={generalError.Error} />;

  // Transform advent contests into weeks format
  const weeks =
    adventContests?.map((contest, index) => ({
      id: index + 1,
      name: contest.name,
      challenges: contest.challenges || [],
      tag: contest.tag,
    })) || [];

  // Get current week's challenges and tag
  const currentWeek = weeks.find((week) => week.id === activeWeek);
  const currentWeekChallenges = currentWeek?.challenges || [];
  const currentContestTag = currentWeek?.tag;

  if (!adventContests?.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-[#D32F2F] text-2xl font-bold mb-4">
            🎄 Challenges Begin December 1st! 🎄
          </div>
          <p className="text-[#3E2723] text-lg font-semibold">
            Check back soon! Want to help create challenges? Message Saman on
            Slack or Linkedin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-['Cabin_Sketch'] text-4xl md:text-5xl text-[#D32F2F] font-bold mb-8 text-center text-shadow-vintage">
        🎄 Daily Challenges 🎄
      </h2>
      {/* <WeekSelector
        weeks={weeks}
        activeWeek={activeWeek}
        onSelectWeek={setActiveWeek}
      /> */}
      <ChallengeGrid
        challenges={currentWeekChallenges}
        contestTag={currentContestTag}
      />

      <style jsx>{`
        .text-shadow-vintage {
          text-shadow: 3px 3px 0px #ffd700;
        }

        .border-5 {
          border-width: 5px;
        }

        .border-3 {
          border-width: 3px;
        }
      `}</style>
    </div>
  );
};

export default ChallengesSection;
